import { ContactMethodName, TrackerDataType } from '../../shared/constants/constants';
import { ContentLogKeys, EngagementKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import {
  DropdownTable,
  FlatContentLog,
  FlatEngagement,
  FlatIssue,
  FlatService,
  Org,
  UserMeta,
} from '../../shared/types';
import dayjs from '../../utils/dayjs';
import { isFlatContentLog, isFlatEngagement, isFlatIssue, isFlatService } from '../../shared/isTypes';
import {
  CalendarDateBasis,
  CalendarEvent,
  CalendarEventChipItem,
  CalendarEventFileItem,
  CalendarEventFileSection,
  CalendarEventLinkedItem,
  ContentEvent,
  EngagementEvent,
  IssueEvent,
  ServiceEvent,
} from './types';

const contactMethodLabelMap: Record<string, string> = {
  [ContactMethodName.NO_INTERMEDIARY]: 'in person',
  [ContactMethodName.PHONE]: 'phone call',
};

export const flattenMonthlyQueryData = <T>(queries: Array<{ data?: T[] }>) =>
  queries.flatMap((query) => (Array.isArray(query.data) ? query.data : []));

export const formatContactMethod = (value?: string | null) => {
  if (!value) return '';
  return contactMethodLabelMap[value] || value;
};

function getCalendarDate(date: string) {
  return dayjs(date).startOf('day').toDate();
}

function getResolvedCalendarDate(
  entryDate: string,
  dueDate: string | null | undefined,
  dateBasis: CalendarDateBasis
): Date | null {
  if (dateBasis === 'entryDate') {
    return getCalendarDate(entryDate);
  }

  if (!dueDate) {
    return getCalendarDate(entryDate);
  }

  return getCalendarDate(dueDate);
}

function getResolvedDueDate(dueDate: string | null | undefined): Date | null {
  if (!dueDate) {
    return null;
  }

  return dayjs(dueDate).toDate();
}

function buildSecondaryMeta(fieldKey: string, label: string, values: string[], iconKey?: 'campaign') {
  return {
    fieldKey,
    label,
    value: values.join(', '),
    iconKey,
  };
}

function getTeamMemberNames(leadIds: string[], dropdownTable: DropdownTable) {
  return dropdownTable.lead.filter((lead: UserMeta) => leadIds.includes(lead._id)).map((lead: UserMeta) => lead.name);
}

function getTeamMemberIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (item && typeof item === 'object' && '_id' in item && typeof item._id === 'string') {
        return item._id;
      }

      return null;
    })
    .filter((id): id is string => Boolean(id));
}

function normalizeTextArray(value: string | string[] | null | undefined) {
  if (Array.isArray(value)) {
    return value.map((item) => `${item}`.trim()).filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapColorItems(
  items: Array<{ name: string; backgroundColor?: string | null }> | null | undefined
): CalendarEventChipItem[] {
  return (items ?? [])
    .map((item) => ({
      name: item.name,
      backgroundColor: item.backgroundColor ?? undefined,
    }))
    .filter((item) => Boolean(item.name));
}

function mapLinkedItems(
  items: Array<{ _id?: string; name?: string; backgroundColor?: string | null }>
): CalendarEventLinkedItem[] {
  return (items ?? [])
    .map((item) => ({
      id: item._id,
      name: item.name ?? '',
      backgroundColor: item.backgroundColor ?? undefined,
    }))
    .filter((item) => Boolean(item.name));
}

function getResolvedNamesFromEntries<T extends { _id?: string; name?: string }>(
  value: unknown,
  resolver: (id: string) => T | undefined
) {
  let entries = [];

  if (Array.isArray(value)) {
    entries = value;
  } else if (value) {
    entries = [value];
  }

  return entries
    .map((entry) => {
      if (!entry) {
        return '';
      }

      if (typeof entry === 'string') {
        return resolver(entry)?.name ?? '';
      }

      if (typeof entry === 'object' && 'name' in entry && typeof entry.name === 'string') {
        return entry.name;
      }

      if (typeof entry === 'object' && '_id' in entry && typeof entry._id === 'string') {
        return resolver(entry._id)?.name ?? '';
      }

      return '';
    })
    .filter(Boolean);
}

function getResolvedNameFromEntry<T extends { _id?: string; name?: string }>(
  value: unknown,
  resolver: (id: string) => T | undefined
) {
  return getResolvedNamesFromEntries(value, resolver).join(', ');
}

function getJournalistFromDropdownTable(journalistId: unknown, dropdownTable: DropdownTable) {
  let resolvedId = null;

  if (typeof journalistId === 'string') {
    resolvedId = journalistId;
  } else if (
    journalistId &&
    typeof journalistId === 'object' &&
    '_id' in journalistId &&
    typeof journalistId._id === 'string'
  ) {
    resolvedId = journalistId._id;
  }

  if (!resolvedId) {
    return null;
  }

  const directMatch = dropdownTable.journalist?.find((journalist) => journalist._id === resolvedId);
  if (directMatch) {
    return directMatch;
  }

  const orgAgentMatch = Object.values(dropdownTable.orgAgents ?? {})
    .flat()
    .find((journalist) => journalist?._id === resolvedId);

  return orgAgentMatch ?? null;
}

function getMediaOutletNameFromValue(value: unknown, dropdownTable: DropdownTable) {
  const directName = getResolvedNameFromEntry(value, (id) => dropdownTable.outlet?.find((org: Org) => org._id === id));
  if (directName) {
    return directName;
  }

  const values = Array.isArray(value) ? value : [value];
  const expandedName = values
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return '';
      }

      if ('name' in entry && typeof entry.name === 'string') {
        return entry.name;
      }

      if ('Org' in entry && typeof entry.Org === 'string') {
        return dropdownTable.outlet?.find((org: Org) => org._id === entry.Org)?.name ?? '';
      }

      return '';
    })
    .filter(Boolean)
    .join(', ');

  return expandedName;
}

function getJournalistNameFromValue(value: unknown, dropdownTable: DropdownTable) {
  const directName = getResolvedNameFromEntry(
    value,
    (id) => getJournalistFromDropdownTable(id, dropdownTable) ?? undefined
  );
  if (directName) {
    return directName;
  }

  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return '';
      }

      if ('name' in entry && typeof entry.name === 'string') {
        return entry.name;
      }

      if ('fullName' in entry && typeof entry.fullName === 'string') {
        return entry.fullName;
      }

      if ('_id' in entry && typeof entry._id === 'string') {
        return getJournalistFromDropdownTable(entry._id, dropdownTable)?.name ?? '';
      }

      return '';
    })
    .filter(Boolean)
    .join(', ');
}

function mapFilesToItems(
  files: Array<{ type?: string; name?: string; description?: string; path?: string }>
): CalendarEventFileItem[] {
  return (files ?? [])
    .filter((file) => Boolean(file?.name) && Boolean(file?.path))
    .map((file) => ({
      type: file.type === 'url' ? 'link' : 'file',
      name: file.name ?? '',
      description: file.description ?? '',
      path: file.path ?? '',
    }));
}

function createFileSections(title: string, items: CalendarEventFileItem[]): CalendarEventFileSection[] {
  if (items.length === 0) {
    return [];
  }

  return [{ title, items }];
}

export function formatServiceCalendarEvent(
  item: FlatService,
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): ServiceEvent | null {
  const entryDate = item.cells[ServiceKeys.entryDate];
  const dueDate = item.cells[ServiceKeys.dueDate];
  const calendarDate = getResolvedCalendarDate(entryDate, dueDate, dateBasis);
  if (calendarDate === null) return null;
  const teamMemberNames = getTeamMemberNames(item.cells[ServiceKeys.teamMember], dropdownTable);
  const teamMemberIds = getTeamMemberIds(item.cells[ServiceKeys.teamMember]);
  const campaignItems = mapColorItems(item.cells[ServiceKeys.campaign]);
  const typeItems = mapColorItems(item.cells[ServiceKeys.type]);
  const unitItems = normalizeTextArray(item.cells[ServiceKeys.units]);
  const departmentItems = normalizeTextArray(item.cells[ServiceKeys.dept]);
  const fileSections = createFileSections('Comms Material', mapFilesToItems(item.cells[ServiceKeys.file] ?? []));

  return {
    logtype: TrackerDataType.SERVICE,
    entryDate: dayjs(entryDate).toDate(),
    dueDate: getResolvedDueDate(dueDate),
    calendarDate,
    lead: item.cells[ServiceKeys.unitLead]
      ? item.cells[ServiceKeys.unitLead].map((expert) => expert.name).join(', ')
      : '',
    unit: unitItems.join(', '),
    campaign: campaignItems.map((camp) => camp.name).join(', '),
    department: departmentItems.join(', '),
    type: typeItems.map((type) => type.name).join(', '),
    teamMembers: teamMemberNames.join(', '),
    teamMemberIds,
    complexity: item.cells[ServiceKeys.complexity] ? item.cells[ServiceKeys.complexity].toString() : '',
    diversity: '',
    status: item.cells[ServiceKeys.status],
    keyMessaging: item.cells[ServiceKeys.keyMessaging] ?? '',
    comms: item.cells[ServiceKeys.file] ?? [],
    id: item.id,
    primaryTitle: item.cells[ServiceKeys.service],
    campaignItems,
    typeItems,
    unitItems,
    departmentItems,
    backgroundHtml: item.cells[ServiceKeys.actions] ?? '',
    fileSections,
    linkedServiceItems: [],
    audienceItems: [],
    tone: null,
    hasLiveLink: false,
    secondaryMeta: buildSecondaryMeta(
      'campaign',
      'Campaign',
      campaignItems.map((camp) => camp.name),
      'campaign'
    ),
  };
}

export function formatInteractionCalendarEvent(
  item: FlatIssue,
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): IssueEvent | null {
  const entryDate = item.cells[IssueKeys.entryDate];
  const dueDate = item.cells[IssueKeys.dueDate];
  const calendarDate = getResolvedCalendarDate(entryDate, dueDate, dateBasis);
  if (calendarDate === null) return null;
  const teamMemberNames = getTeamMemberNames(item.cells[IssueKeys.lead], dropdownTable);
  const teamMemberIds = getTeamMemberIds(item.cells[IssueKeys.lead]);
  const journalistMeta = getJournalistFromDropdownTable(item.cells[IssueKeys.journalist], dropdownTable);
  const outletNameFromCell = getMediaOutletNameFromValue(item.cells[IssueKeys.outlet], dropdownTable);
  const outletNameFromJournalist = journalistMeta?.Org
    ? dropdownTable.outlet?.find((org: Org) => org._id === journalistMeta.Org)?.name ?? ''
    : '';
  const outletName = outletNameFromCell || outletNameFromJournalist;
  const journalistName =
    getJournalistNameFromValue(item.cells[IssueKeys.journalist], dropdownTable) || journalistMeta?.name || '';
  const campaignItems = mapColorItems(item.cells[IssueKeys.campaign]);
  const typeItems = mapColorItems(item.cells[IssueKeys.type]);
  const unitItems = normalizeTextArray(item.cells[IssueKeys.units]);
  const departmentItems = normalizeTextArray(item.cells[IssueKeys.dept]);
  const fileSections = createFileSections('Story Link', mapFilesToItems(item.cells[IssueKeys.file] ?? []));
  const linkedServiceItems = mapLinkedItems(item.cells[IssueKeys.linkedService] ?? []);

  return {
    logtype: TrackerDataType.MEDIA_INTERACTION,
    entryDate: dayjs(entryDate).toDate(),
    dueDate: getResolvedDueDate(dueDate),
    calendarDate,
    lead: item.cells[IssueKeys.expert].map((expert) => expert.name).join(', '),
    unit: unitItems.join(', '),
    type: typeItems.map((type) => type.name).join(', '),
    campaign: campaignItems.map((campaign) => campaign.name).join(', '),
    department: departmentItems.join(', '),
    status: item.cells[IssueKeys.status],
    teamMembers: teamMemberNames.join(', '),
    teamMemberIds,
    contact: formatContactMethod(item.cells[IssueKeys.contactMethod]),
    journalist: journalistName,
    keyMessaging: item.cells[IssueKeys.keyMessaging] ?? '',
    outlet: outletName,
    comms: item.cells[IssueKeys.file],
    id: item.id,
    primaryTitle: item.cells[IssueKeys.topic],
    campaignItems,
    typeItems,
    unitItems,
    departmentItems,
    backgroundHtml: item.cells[IssueKeys.response] ?? '',
    fileSections,
    linkedServiceItems,
    audienceItems: [],
    tone: item.cells[IssueKeys.tone] ?? null,
    hasLiveLink: false,
    secondaryMeta: buildSecondaryMeta(
      'campaign',
      'Campaign',
      campaignItems.map((campaign) => campaign.name),
      'campaign'
    ),
  };
}

export function formatEngagementCalendarEvent(
  item: FlatEngagement,
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): EngagementEvent | null {
  const engagementCells = item.cells as Record<string, any>;
  const entryDate = engagementCells[EngagementKeys.entryDate] ?? engagementCells[EngagementKeys.date];
  const dueDate = engagementCells[EngagementKeys.dueDate];
  const calendarDate = getResolvedCalendarDate(entryDate, dueDate, dateBasis);
  if (calendarDate === null) return null;
  const teamMemberNames = getTeamMemberNames(item.cells[EngagementKeys.lead], dropdownTable);
  const teamMemberIds = getTeamMemberIds(item.cells[EngagementKeys.lead]);
  const organizationNames = getResolvedNamesFromEntries(item.cells[EngagementKeys.organization], (id) =>
    dropdownTable.organization?.find((org) => org._id === id)
  );
  const communityMemberNames = getResolvedNamesFromEntries(item.cells[EngagementKeys.communityMember], (id) =>
    dropdownTable.communityMember?.find((member) => member._id === id)
  );
  const campaignItems = mapColorItems(item.cells[EngagementKeys.campaign]);
  const typeItems = mapColorItems(item.cells[EngagementKeys.type]);
  const unitItems = normalizeTextArray(item.cells[EngagementKeys.units]);
  const departmentItems = normalizeTextArray(item.cells[EngagementKeys.dept]);
  const fileSections = createFileSections('Documentation', mapFilesToItems(item.cells[EngagementKeys.file] ?? []));
  const linkedServiceItems = mapLinkedItems(item.cells[EngagementKeys.linkedService] ?? []);

  return {
    logtype: TrackerDataType.ENGAGEMENT,
    entryDate: dayjs(entryDate).toDate(),
    dueDate: getResolvedDueDate(dueDate),
    calendarDate,
    lead: item.cells[EngagementKeys.expert].map((expert) => expert.name).join(', '),
    unit: unitItems.join(', '),
    campaign: campaignItems.map((campaign) => campaign.name).join(', '),
    department: departmentItems.join(', '),
    type: typeItems.map((type) => type.name).join(', '),
    teamMembers: teamMemberNames.join(', '),
    teamMemberIds,
    contact: formatContactMethod(item.cells[EngagementKeys.contactMethod]),
    organization: organizationNames.join(', '),
    communityMember: communityMemberNames.join(', '),
    status: item.cells[EngagementKeys.status],
    keyMessaging: item.cells[EngagementKeys.notes] ?? '',
    comms: item.cells[EngagementKeys.file] ?? [],
    id: item.id,
    primaryTitle: item.cells[EngagementKeys.topic],
    campaignItems,
    typeItems,
    unitItems,
    departmentItems,
    backgroundHtml: item.cells[EngagementKeys.background] ?? '',
    fileSections,
    linkedServiceItems,
    audienceItems: [],
    tone: item.cells[EngagementKeys.tone] ?? null,
    hasLiveLink: false,
    secondaryMeta: buildSecondaryMeta(
      'campaign',
      'Campaign',
      campaignItems.map((campaign) => campaign.name),
      'campaign'
    ),
  };
}

export function formatContentCalendarEvent(
  item: FlatContentLog,
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): ContentEvent | null {
  const entryDate = item.cells[ContentLogKeys.entryDate];
  const dueDate = item.cells[ContentLogKeys.dueDate];
  const calendarDate = getResolvedCalendarDate(entryDate, dueDate, dateBasis);
  if (calendarDate === null) return null;
  const teamMemberNames = getTeamMemberNames(item.cells[ContentLogKeys.lead], dropdownTable);
  const teamMemberIds = getTeamMemberIds(item.cells[ContentLogKeys.lead]);
  const campaignItems = mapColorItems(item.cells[ContentLogKeys.campaign]);
  const typeItems = mapColorItems(item.cells[ContentLogKeys.type]);
  const unitItems = normalizeTextArray(item.cells[ContentLogKeys.units]);
  const departmentItems = normalizeTextArray(item.cells[ContentLogKeys.dept]);
  const audienceItems: Array<CalendarEventLinkedItem | null> = (item.cells[ContentLogKeys.audience] ?? []).map(
    (audience) => {
      if (typeof audience === 'string') {
        const audienceMeta = dropdownTable.audience?.find((candidate) => candidate._id === audience);
        if (audienceMeta?._id && audienceMeta.name) {
          return {
            id: audienceMeta._id,
            name: audienceMeta.name,
            backgroundColor: audienceMeta.backgroundColor ?? undefined,
          };
        }
        return null;
      }

      if (audience._id && audience.name) {
        const audienceMeta = dropdownTable.audience?.find((candidate) => candidate._id === audience._id);
        return {
          id: audience._id,
          name: audience.name,
          backgroundColor: audienceMeta?.backgroundColor ?? undefined,
        };
      }
      return null;
    }
  );
  const normalizedAudienceItems: CalendarEventLinkedItem[] = audienceItems.filter(
    (audience): audience is CalendarEventLinkedItem =>
      Boolean(audience && typeof audience.id === 'string' && audience.name)
  );
  const linkedServiceItems = mapLinkedItems(item.cells[ContentLogKeys.linkedService] ?? []);
  const keyMessaging =
    item.cells[ContentLogKeys.content] ??
    item.cells[ContentLogKeys.otherNotes] ??
    item.cells[ContentLogKeys.background] ??
    '';
  const fileSections = createFileSections('Drafts, Images, and Links', [
    ...mapFilesToItems(item.cells[ContentLogKeys.file] ?? []),
    ...(item.cells[ContentLogKeys.liveLink]?.name && item.cells[ContentLogKeys.liveLink]?.path
      ? [
          {
            type: 'link' as const,
            name: item.cells[ContentLogKeys.liveLink].name,
            description: item.cells[ContentLogKeys.liveLink].description ?? '',
            path: item.cells[ContentLogKeys.liveLink].path,
          },
        ]
      : []),
  ]);

  return {
    logtype: TrackerDataType.CONTENT_LOG,
    entryDate: dayjs(entryDate).toDate(),
    dueDate: getResolvedDueDate(dueDate),
    calendarDate,
    lead: item.cells[ContentLogKeys.expert]
      .map((expert) =>
        typeof expert === 'string'
          ? dropdownTable.expert?.find((candidate) => candidate._id === expert)?.name
          : expert?.name
      )
      .filter(Boolean)
      .join(', '),
    unit: unitItems.join(', '),
    campaign: campaignItems.map((campaign) => campaign.name).join(', '),
    department: departmentItems.join(', '),
    type: typeItems.map((type) => type.name).join(', '),
    teamMembers: teamMemberNames.join(', '),
    teamMemberIds,
    status: item.cells[ContentLogKeys.status],
    keyMessaging,
    comms: item.cells[ContentLogKeys.file] ?? [],
    id: item.id,
    primaryTitle: item.cells[ContentLogKeys.topic],
    campaignItems,
    typeItems,
    unitItems,
    departmentItems,
    backgroundHtml: item.cells[ContentLogKeys.background] ?? '',
    fileSections,
    linkedServiceItems,
    audienceItems: normalizedAudienceItems,
    tone: null,
    hasLiveLink: Boolean(item.cells[ContentLogKeys.liveLink]?.path),
    secondaryMeta: buildSecondaryMeta(
      'campaign',
      'Campaign',
      campaignItems.map((campaign) => campaign.name),
      'campaign'
    ),
  };
}

function formatCalendarEvent(
  item: FlatIssue | FlatService | FlatEngagement | FlatContentLog,
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): CalendarEvent | null {
  if (!item?.cells) {
    console.error('Invalid item format');
    return null;
  }

  if (isFlatService(item)) {
    return formatServiceCalendarEvent(item, dropdownTable, dateBasis);
  }

  if (isFlatEngagement(item)) {
    return formatEngagementCalendarEvent(item, dropdownTable, dateBasis);
  }

  if (isFlatContentLog(item)) {
    return formatContentCalendarEvent(item, dropdownTable, dateBasis);
  }

  if (isFlatIssue(item)) {
    return formatInteractionCalendarEvent(item, dropdownTable, dateBasis);
  }

  return null;
}

export function formatCalendarEvents(
  data: FlatIssue[] | FlatService[] | FlatEngagement[] | FlatContentLog[],
  dropdownTable: DropdownTable,
  dateBasis: CalendarDateBasis
): CalendarEvent[] {
  if (data == null) {
    return [];
  }

  return data
    .map((item) => formatCalendarEvent(item, dropdownTable, dateBasis))
    .filter((item: CalendarEvent | null): item is CalendarEvent => item !== null);
}

export function groupEventsByDay(events: CalendarEvent[]) {
  return events.reduce<Record<string, CalendarEvent[]>>((groupedEvents, event) => {
    const dayKey = dayjs(event.calendarDate).format('YYYY-MM-DD');
    const existingEvents = groupedEvents[dayKey] ?? [];

    groupedEvents[dayKey] = [...existingEvents, event];
    return groupedEvents;
  }, {});
}
